import { useState, useRef, useCallback } from 'react';
import {
  adjustPlayerElo,
  createPlayer,
  deletePlayerCommand,
  persistEloAdjustments,
  persistPlayers,
  playerCommandsAreServerAuthoritative,
  updatePlayerProfile,
} from '../lib/api/players';
import { SaveRefused, saveWithRetry } from '../lib/saveWithRetry';
import { errMsg } from '../lib/errors';

// ─────────────────────────────────────────────────────────────────────
// usePlayers — the player/ELO domain, extracted from the former monolith's
// Padel24AppMain (see docs/history/DECOMPOSITION_PLAN.md, PR-E). Owns three persisted slices:
//   • allPlayers      ('players_v1', GLOBAL/unscoped) — the shared player DB
//   • singleMatches   ('single_matches_v1')           — matches outside tournaments
//   • eloAdjustments  ('elo_adjustments_v1', GLOBAL)  — manual ELO-change audit log
//
// None has a §13.20 blanket-persist flag — writes go through explicit mutators.
// As with useArchive/useBookingSettings, the INITIAL load lives in the
// atomic mount loader (which flushes via setAllPlayers/setSingleMatches/
// setEloAdjustments + runs the player migrations), so this hook exposes those
// setters as a loader bridge.
//
// SCOPE: this hook owns the state + the five engine-free player-CRUD mutators
// (saveAllPlayers, persistNewPlayerImmediate, addPlayer, updatePlayer,
// deletePlayer — the last carries the lone eloAdjustments write). The three
// SINGLE-MATCH mutators (addSingleMatch/deleteSingleMatch/updateSingleMatch)
// live elsewhere in src/: they call the `Padel24Engine.singleMatch.*`
// (NOT extracted to src/engine) and read `liveAllPlayers` (which depends on the
// tournament state). They use the exposed
// setSingleMatches/setAllPlayers. The derivations (liveAllPlayers/
// effectivePlayerDB/combinedArchive) and the cross-domain ELO-sync writers
// (archiveTournament, seriespill season transition) also live elsewhere, using the
// exposed setAllPlayers. All mutator logic below is copied verbatim from the
// former monolith — including the deliberate Storage-ordering mix (saveAllPlayers /
// addPlayer / persistNewPlayerImmediate / the eloAdjustments write are
// setState-first/optimistic; do NOT "fix" to Storage-first).
// ─────────────────────────────────────────────────────────────────────

type ToastFn = (type: string, message: string, duration?: number) => void;

export function usePlayers(showToast: ToastFn) {
  const [allPlayers, setAllPlayersState] = useState<any[]>([]); // all players (system + custom combined)
  // R5 SYNC MIRROR for players — the same mechanism useBookings uses for
  // `bookingsRef`, and for the same reason (#273).
  //
  // A writer that has to build its next list OUTSIDE a React state updater —
  // because it must `await` a Storage write before committing, and an updater
  // must stay pure — cannot read `allPlayers` from its closure: that is the
  // value from the last render, and an earlier action in the same tick may
  // already have queued a newer one. The updater's `prev` never had that
  // problem, so removing the updater must not lose it.
  //
  // The ref is maintained by the setter below rather than by discipline at each
  // call site. `bookingsRef` relies on "EVERY setBookings MUST be paired with
  // bookingsRef.current = <sameNewList>" and says so in capitals; there are 13
  // `setAllPlayers` call sites across five files, so the pairing is done ONCE,
  // here, where it cannot be forgotten.
  const allPlayersRef = useRef<any[]>([]);

  /**
   * `setAllPlayers`, with the mirror kept in lockstep — SYNCHRONOUSLY, for both
   * call forms.
   *
   * The updater is resolved HERE, against `allPlayersRef.current`, rather than
   * being handed to React. That is the whole difference, and it matters twice:
   *
   *   1. React runs an updater at FLUSH time, not at call time. Assigning the
   *      mirror inside one leaves it stale for anybody reading on the next line
   *      — which is precisely what the mirror exists to prevent.
   *   2. Worse, it lets the mirror DIVERGE from the state. Call the updater form
   *      and then the value form in one tick and React applies the updater first
   *      (mirror := updater's result) and the value second (state := the value);
   *      the mirror is left holding the EARLIER write. Order inverted, silently.
   *
   * Resolving against the ref is sound because the ref is the authoritative
   * latest list: every write goes through this setter, so it is never behind the
   * state, and inside a batch it is AHEAD of React's `prev` — which is the
   * property the archive commit needs (#273).
   */
  const setAllPlayers = useCallback((next: any) => {
    const value = typeof next === 'function' ? next(allPlayersRef.current) : next;
    // Mirror first, then the state call. The two are in one synchronous block
    // with nothing between them, so swapping them is not observable today and
    // no test can tell the difference — it stays this way only to keep the ref
    // correct if React ever re-rendered synchronously inside the setter.
    allPlayersRef.current = value;
    setAllPlayersState(value);
  }, []);
  const [singleMatches, setSingleMatches] = useState<any[]>([]); // single matches outside tournaments
  // Planned (not-yet-played) single matches created from a court booking.
  // A PlannedMatch is the INPUT shape `singleMatch.createSingleMatch` expects
  // (team1/team2/eloWeightPct) plus a `bookingId` linkage — NO sets/result yet.
  // Kept in a SEPARATE store ('planned_matches_v1') so the result-bearing
  // single_matches_v1 consumers (useDerivations.liveAllPlayers etc.) stay
  // untouched. The result is entered later via usePlannedMatchActions, which
  // routes through the existing addSingleMatch pipeline.
  const [plannedMatches, setPlannedMatches] = useState<any[]>([]);
  // Manual ELO adjustments made by an admin (outside of matches). Each entry:
  // { id, playerId, playerName, oldElo, newElo, change, adjustedAt, reason }
  const [eloAdjustments, setEloAdjustments] = useState<any[]>([]);

  const saveAllPlayers = async (list: any[]) => {
    setAllPlayers(list);
    try {
      await persistPlayers(list);
    } catch (e) {
      console.error('[Padel24] Kunne ikke lagre spillere:', e);
      showToast('error', 'Kunne ikke lagre til database: ' + (errMsg(e) || 'ukjent feil'));
      throw e;
    }
  };

  // Persist a player that already has an id and all fields (typically from
  // the setup wizards that generate their own IDs). Differs from addPlayer
  // in that we do NOT generate a new id or default fields — we take a
  // complete object and put it straight into the DB. This fixes an earlier
  // problem where players added in KM-setup disappeared on a page refresh
  // if the user did not save the setup explicitly.
  const persistNewPlayerImmediate = async (player: any) => {
    if (!player || !player.id || !player.name) return false;
    if (playerCommandsAreServerAuthoritative()) {
      // Create-or-update ONE player. The setup wizards reference the id they
      // generated immediately, which is why this exists at all — so the id
      // stays the client's and the server refuses a collision rather than
      // assigning its own.
      try {
        const exists = (allPlayersRef.current || allPlayers).some((p) => p.id === player.id);
        const saved = exists
          ? await saveWithRetry(() => updatePlayerProfile(String(player.id), player))
          : await saveWithRetry(() => createPlayer(player));
        setAllPlayers((prev) =>
          prev.some((p) => p.id === saved.id)
            ? prev.map((p) => (p.id === saved.id ? saved : p))
            : [...prev, saved],
        );
        return true;
      } catch (e) {
        console.error('[Padel24] Kunne ikke persistere ny spiller fra setup:', e);
        showToast(
          'error',
          e instanceof SaveRefused
            ? e.message
            : 'Kunne ikke lagre spiller til DB: ' + (errMsg(e) || 'ukjent feil'),
        );
        return false;
      }
    }
    let newList = null;
    setAllPlayers((prev) => {
      // If the player already exists (same id), update instead of duplicating
      if (prev.some((p) => p.id === player.id)) {
        newList = prev.map((p) => (p.id === player.id ? { ...p, ...player } : p));
      } else {
        newList = [...prev, player];
      }
      return newList;
    });
    try {
      await Promise.resolve();
      if (newList) {
        await persistPlayers(newList);
      }
      return true;
    } catch (e) {
      console.error('[Padel24] Kunne ikke persistere ny spiller fra setup:', e);
      showToast('error', 'Kunne ikke lagre spiller til DB: ' + (errMsg(e) || 'ukjent feil'));
      return false;
    }
  };

  const addPlayer = async (player: any) => {
    const id = player.id || `c_${Date.now()}_${Math.floor(Math.random() * 10000)}`;
    const newPlayer = {
      id,
      name: player.name,
      firstName: player.firstName,
      lastName: player.lastName,
      email: player.email || undefined,
      gender: player.gender || 'M',
      elo: player.elo || 1500,
      level: Math.min(9, Math.max(1, Math.floor(((player.elo || 1500) - 500) / 300) + 1)),
      isSystem: false,
      isFavorite: false,
      lastPlayed: 0,
      tournamentsPlayed: 0,
      matchesPlayed: 0, // Newbie tracking: new player, no matches yet
      lastMatchAt: null,
      createdAt: Date.now(),
    };
    // Functional setState guarantees we spread from the CURRENT state,
    // not from a stale closure variable. We pull newList out of the callback
    // and use it to persist to Storage afterwards.
    if (playerCommandsAreServerAuthoritative()) {
      // ONE PLAYER, NAMED. This used to append to the club's copy of the GLOBAL
      // list and write the whole thing back, so it deleted every player created
      // elsewhere since this tab loaded. The server derives the level and the
      // defaults, and refuses an id that already exists.
      try {
        const saved = await saveWithRetry(() => createPlayer(newPlayer));
        setAllPlayers((prev) => [...prev, saved]);
        showToast('success', `${saved.name} er lagt til`);
      } catch (e) {
        console.error('[Padel24] Kunne ikke lagre ny spiller:', e);
        showToast(
          'error',
          e instanceof SaveRefused ? e.message : 'Lagring feilet: ' + (errMsg(e) || 'ukjent feil'),
        );
      }
      return;
    }
    let newList = null;
    setAllPlayers((prev) => {
      newList = [...prev, newPlayer];
      return newList;
    });
    try {
      await Promise.resolve(); // let React's commit phase finish
      if (newList) {
        await persistPlayers(newList);
        showToast('success', `${newPlayer.name} er lagt til`);
      }
    } catch (e) {
      console.error('[Padel24] Kunne ikke lagre ny spiller:', e);
      showToast('error', 'Lagring feilet: ' + (errMsg(e) || 'ukjent feil'));
    }
  };

  const updatePlayer = async (id: string, updates: any) => {
    const existing = allPlayers.find((p) => p.id === id);

    // If ELO is changed manually — log an adjustment entry so it
    // shows up in the player's history (separates manual changes from match ELO).
    // updates.__adjustReason is an optional reason from the admin (not stored
    // on the player — only in the adjustment log).
    const { __adjustReason, ...cleanUpdates } = updates;
    const movesElo = !!existing && updates.elo != null && updates.elo !== existing.elo;

    if (playerCommandsAreServerAuthoritative()) {
      // TWO WRITES BECOME TWO COMMANDS, and the rating one is atomic with its
      // own audit entry. The browser wrote the adjustment log through a
      // whole-list PUT whose failure it CAUGHT AND IGNORED, then the player
      // through another — so a rating could move with the only record of why it
      // moved silently lost, and either write could erase another club's work.
      try {
        const { elo: _elo, ...profile } = cleanUpdates;
        if (Object.keys(profile).length > 0) {
          const saved = await saveWithRetry(() => updatePlayerProfile(String(id), profile));
          setAllPlayers((prev) => prev.map((p) => (p.id === saved.id ? saved : p)));
        }
        if (movesElo) {
          const { player, adjustment } = await saveWithRetry(() =>
            adjustPlayerElo(String(id), Number(updates.elo), (__adjustReason || '').trim() || null),
          );
          setAllPlayers((prev) => prev.map((p) => (p.id === player.id ? player : p)));
          // Adopted, not recomputed: the server owns the id, the timestamp and
          // the arithmetic of the entry that explains the change.
          setEloAdjustments((prev) => [...prev, adjustment]);
        }
      } catch (e) {
        console.error('[Padel24] Kunne ikke lagre spiller:', e);
        showToast(
          'error',
          e instanceof SaveRefused
            ? e.message
            : 'Kunne ikke lagre til database: ' + (errMsg(e) || 'ukjent feil'),
        );
        throw e;
      }
      return;
    }

    // If ELO is changed manually — log an adjustment entry so it
    // shows up in the player's history (separates manual changes from match ELO).
    if (movesElo) {
      const adjustment = {
        id: `eloadj_${Date.now()}_${Math.floor(Math.random() * 100000)}`,
        playerId: id,
        playerName: existing.name,
        oldElo: existing.elo,
        newElo: updates.elo,
        change: updates.elo - existing.elo,
        adjustedAt: Date.now(),
        reason: (__adjustReason || '').trim() || null,
      };
      const newAdjustments = [...eloAdjustments, adjustment];
      setEloAdjustments(newAdjustments);
      try {
        await persistEloAdjustments(newAdjustments);
      } catch (e) {
        console.error('[Padel24] Kunne ikke lagre ELO-justering:', e);
      }
    }

    const list = allPlayers.map((p) => (p.id === id ? { ...p, ...cleanUpdates } : p));
    await saveAllPlayers(list);
  };

  const deletePlayer = async (id: string) => {
    // Protection: do not allow system players to be deleted
    const player = allPlayers.find((p) => p.id === id);
    if (player?.isSystem) {
      console.warn('[Padel24] Kan ikke slette system-spiller:', player.name);
      return;
    }
    if (playerCommandsAreServerAuthoritative()) {
      // The guard above stays — it keeps the button honest — but it is no
      // longer the only thing standing between a system player and deletion:
      // the server refuses one, and the route is behind the platform role
      // because this table has no tenant column (#272).
      try {
        await saveWithRetry(() => deletePlayerCommand(String(id)));
        setAllPlayers((prev) => prev.filter((p) => p.id !== id));
      } catch (e) {
        console.error('[Padel24] Kunne ikke slette spiller:', e);
        showToast(
          'error',
          e instanceof SaveRefused
            ? e.message
            : 'Kunne ikke slette spilleren: ' + (errMsg(e) || 'ukjent feil'),
        );
      }
      return;
    }
    await saveAllPlayers(allPlayers.filter((p) => p.id !== id));
  };

  return {
    allPlayers,
    // The synchronous mirror. Read it — not `allPlayers` — from any writer that
    // builds its next list outside a React updater (#273).
    allPlayersRef,
    singleMatches,
    plannedMatches,
    eloAdjustments,
    // Exposed for the atomic mount loader, the single-match mutators + the
    // cross-domain ELO-sync writers that all live elsewhere in src/.
    setAllPlayers,
    setSingleMatches,
    setPlannedMatches,
    setEloAdjustments,
    saveAllPlayers,
    persistNewPlayerImmediate,
    addPlayer,
    updatePlayer,
    deletePlayer,
  };
}
